// EN-4743. AAMVA barcode parsing for the PDF417 on the back of a US / Canadian
// driver's licence or state ID.
//
// Port of the kiosk's parser (visitly-kiosk-app/src/utils/aamva.js @ d4e6219,
// including the version 02/03 Canadian card fixes), logic unchanged, so both apps
// read a card the same way. Only types were added.
//
// PDF417 carries Reed-Solomon error correction, so the decoder either hands back
// the exact bytes or fails outright; there is no "probably an O, maybe a 0"
// failure mode the way there is with OCR.
//
// Spec: AAMVA DL/ID Card Design Standard. The payload is a header, one or more
// subfile designators, then newline-separated data elements, each a three-letter
// element ID followed by its value.

// Only the elements this app has a use for. The spec defines dozens more.
const ELEMENT = {
  FAMILY_NAME: 'DCS',
  FIRST_NAME: 'DAC',
  MIDDLE_NAME: 'DAD',
  // Jr/Sr/III and the like, stored as its own element.
  NAME_SUFFIX: 'DCU',
  DOCUMENT_NUMBER: 'DAQ',
  DATE_OF_BIRTH: 'DBB',
  EXPIRY_DATE: 'DBA',
  ISSUE_DATE: 'DBD',
  // Pre-2000 payloads carry one combined name field instead of DCS/DAC/DAD.
  LEGACY_FULL_NAME: 'DAA',
  // AAMVA versions 02 and 03 put first and middle names together in DCT. DAC only
  // became the first-name element in version 04 (2009). Ontario, Quebec and
  // British Columbia still issue on the 2005 standard.
  GIVEN_NAMES: 'DCT',
  // Truncation flags: a long name is stored clipped and flagged 'T'.
  FAMILY_NAME_TRUNCATED: 'DDE',
  FIRST_NAME_TRUNCATED: 'DDF',
  MIDDLE_NAME_TRUNCATED: 'DDG',
} as const;

// The spec's stand-in for "this person has no middle name", stored literally.
const ABSENT_VALUE = 'NONE';

const clean = (value: string | undefined): string => {
  const trimmed = (value ?? '').trim();
  return trimmed.toUpperCase() === ABSENT_VALUE ? '' : trimmed;
};

export interface AamvaHeader {
  issuerId: string;
  version: string;
  declaredLength: number | null;
  actualLength: number;
  isTruncated: boolean;
}

/**
 * Read the AAMVA header without touching any personal data: the issuer (IIN),
 * the standard version, and how long the payload claims to be. A payload shorter
 * than it declares, and not ending on a segment terminator, was cut off and is
 * refused. Nothing returned here identifies the visitor, so it is safe to log.
 *
 * @returns null when there is no AAMVA header to read.
 */
export const inspectAamvaHeader = (raw: unknown): AamvaHeader | null => {
  if (typeof raw !== 'string') return null;
  // IIN (6), version (2), then - from version 02 onwards - a jurisdiction
  // version (2), then the subfile count (2), then one 10-character designator
  // per subfile: type (2), offset (4), length (4).
  const header = raw.match(/ANSI (\d{6})(\d{2})\d{2,4}((?:[A-Z]{2}\d{8})+)/);
  if (!header) return null;

  const [, issuerId, version, designators] = header;
  let declaredLength: number | null = null;
  for (const designator of designators.match(/[A-Z]{2}\d{8}/g) ?? []) {
    const end = Number(designator.slice(2, 6)) + Number(designator.slice(6, 10));
    if (declaredLength === null || end > declaredLength) declaredLength = end;
  }
  // Offsets count from the '@' that opens the payload. Some scanners drop that
  // leading compliance indicator, so measure from 'ANSI' plus the fixed lead-in
  // ('@', LF, RS, CR - 4 bytes) rather than trusting where the string starts.
  const actualLength = raw.length - raw.indexOf('ANSI ') + 4;

  return {
    issuerId,
    version,
    declaredLength,
    actualLength,
    // Two signals have to agree before a payload is called cut off: a short count,
    // and no segment terminator (CR or LF) at the end.
    isTruncated: declaredLength !== null && actualLength < declaredLength && !/[\r\n]\s*$/.test(raw),
  };
};

// Every element ID this parser looks for, as one alternation. Used to find where
// the header stops and the real data starts.
const KNOWN_ELEMENT_IDS = Object.values(ELEMENT).join('|');

/**
 * Drop the header so the remainder can be read a line at a time. The header
 * runs straight into the first subfile with no newline
 * (`ANSI 636014080002DL00410288ZC03290015DLDAQD1234562`), so start where a
 * subfile designator butts directly against a known element ID.
 */
const stripHeader = (payload: string): string => {
  const subfileStart = payload.search(new RegExp('(?:DL|ID)(?:' + KNOWN_ELEMENT_IDS + ')'));
  // No match means an older layout that already puts each element on its own line.
  return subfileStart >= 0 ? payload.slice(subfileStart) : payload;
};

/**
 * Split the payload into { elementId: value }. The first element of a subfile
 * has the designator ('DL' or 'ID') in front of it with no separator; no AAMVA
 * element ID begins with 'DL' or 'ID', so stripping it cannot eat a real element.
 */
const toElementMap = (payload: string): Record<string, string> => {
  const elements: Record<string, string> = {};

  stripHeader(payload)
    .split(/[\r\n]+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .forEach((line) => {
      const match = line.match(/^(?:DL|ID)?([A-Z]{3})(.*)$/);
      if (!match) return;
      const [, elementId, value] = match;
      // First occurrence wins: a repeated element must not overwrite a good read.
      if (elements[elementId] === undefined) elements[elementId] = value;
    });

  return elements;
};

// Pre-2000 fallback: one field holding the whole name, separated by ',' or '$',
// family name first.
const splitLegacyFullName = (fullName: string) => {
  const [family = '', first = '', middle = ''] = fullName.split(/[,$]/).map((part) => part.trim());
  return { family, first, middle };
};

export interface AamvaBarcode {
  firstName: string;
  lastName: string;
  middleName: string;
  nameSuffix: string;
  documentNumber: string;
  // The 8 raw digits the card stores (MMDDCCYY US, CCYYMMDD Canada); see idDates.ts.
  dateOfBirth: string;
  expiryDate: string;
  issueDate: string;
  isNameTruncated: boolean;
  // Provenance, for telemetry. None of it identifies the visitor.
  issuerId: string;
  aamvaVersion: string;
  givenNameSource: 'DAC' | 'DCT' | 'DAA' | '';
}

/**
 * Parse a decoded PDF417 payload from a US / Canadian driver's licence or state ID.
 *
 * @returns null when the payload is not AAMVA, was cut off, or carries no usable name.
 */
export const parseAamvaBarcode = (raw: unknown): AamvaBarcode | null => {
  if (typeof raw !== 'string' || !raw.trim()) return null;

  // Every compliant payload announces itself with 'ANSI ' (or the older 'AAMVA'),
  // so a QR code or other barcode in frame is rejected here.
  if (!/\b(ANSI|AAMVA)\b/.test(raw)) return null;

  const header = inspectAamvaHeader(raw);
  if (header?.isTruncated) return null;

  const elements = toElementMap(raw);

  let lastName = clean(elements[ELEMENT.FAMILY_NAME]);
  let firstName = clean(elements[ELEMENT.FIRST_NAME]);
  let middleName = clean(elements[ELEMENT.MIDDLE_NAME]);
  let givenNameSource: AamvaBarcode['givenNameSource'] = firstName ? 'DAC' : '';

  // Version 02/03 cards: first and middle names share DCT ("MARIE,ANNE" or
  // "MARIE ANNE"). The first token is the first name; the rest are middles,
  // unless the card also carried DAD, which then wins.
  if (!firstName && elements[ELEMENT.GIVEN_NAMES]) {
    const [given = '', ...rest] = clean(elements[ELEMENT.GIVEN_NAMES])
      .split(/[,\s]+/)
      .filter(Boolean);
    firstName = given;
    if (!middleName) middleName = rest.join(' ');
    givenNameSource = firstName ? 'DCT' : '';
  }

  if (!firstName && !lastName && elements[ELEMENT.LEGACY_FULL_NAME]) {
    const legacy = splitLegacyFullName(clean(elements[ELEMENT.LEGACY_FULL_NAME]));
    lastName = legacy.family;
    firstName = legacy.first;
    middleName = legacy.middle;
    givenNameSource = firstName ? 'DAA' : '';
  }

  // No name means nothing this feature can use: report a failed read so the
  // caller falls back to images only.
  if (!firstName && !lastName) return null;

  const isTruncated = (elementId: string) => clean(elements[elementId]).toUpperCase() === 'T';

  return {
    firstName,
    lastName,
    middleName,
    nameSuffix: clean(elements[ELEMENT.NAME_SUFFIX]),
    documentNumber: clean(elements[ELEMENT.DOCUMENT_NUMBER]),
    dateOfBirth: clean(elements[ELEMENT.DATE_OF_BIRTH]),
    expiryDate: clean(elements[ELEMENT.EXPIRY_DATE]),
    issueDate: clean(elements[ELEMENT.ISSUE_DATE]),
    isNameTruncated:
      isTruncated(ELEMENT.FAMILY_NAME_TRUNCATED) ||
      isTruncated(ELEMENT.FIRST_NAME_TRUNCATED) ||
      isTruncated(ELEMENT.MIDDLE_NAME_TRUNCATED),
    issuerId: header?.issuerId ?? '',
    aamvaVersion: header?.version ?? '',
    givenNameSource,
  };
};
